/**
 * DATEV-Export enumerations — the format-agnostic glue between the candidate
 * reader (which decides *which* invoices to export) and the writer (which
 * decides *how* to serialise them).
 *
 * Naming policy (see CLAUDE.md → "Naming convention"): general concepts like
 * `ExportMode`, `ExportTrigger`, `PayerType` use English. DATEV terms of art
 * (`Gewinnermittlungsart`, `Versteuerungsart`, `Kontenrahmen`, BU-Schlüssel)
 * stay German because they map 1:1 to the DATEV EXTF spec.
 */
/**
 * Which voucher-event stream the candidate reader walks for a given run.
 *
 * - `BALANCE_SHEET_ISSUED_INVOICES` (Bilanz): one ISSUED-event Buchungssatz
 *   per invoice. Belegdatum = `issuedAt`. The exporter assumes Soll-
 *   Versteuerung; OPOS handoff happens out-of-band.
 * - `EUR_PAYMENT_RECEIPTS` (EÜR): one Buchungssatz per Zahlung, Belegdatum =
 *   Zahlungsdatum, and a counter-booking per reversed exported Zahlung
 *   (TM-9111). The Zufluss-/Abfluss-Prinzip is honoured by the Zahlung being
 *   the Beleg.
 *
 * v1 ships these two modes. The compound
 * `BALANCE_SHEET_OPOS_INVOICES_AND_PAYMENTS` mode (issue + payment events
 * together) is explicitly deferred.
 */
export var DatevExportMode;
(function (DatevExportMode) {
    DatevExportMode["BALANCE_SHEET_ISSUED_INVOICES"] = "BALANCE_SHEET_ISSUED_INVOICES";
    DatevExportMode["EUR_PAYMENT_RECEIPTS"] = "EUR_PAYMENT_RECEIPTS";
})(DatevExportMode || (DatevExportMode = {}));
/**
 * Which event on an object's timeline produced this Buchungssatz. Stored on
 * `ExportedVoucher` so the same object can yield distinct rows over its
 * lifetime without colliding in the delta-uniqueness check.
 *
 * - `ISSUED` — a Rechnung was issued (Bilanz).
 * - `PAID` — a Zahlung was received (EÜR). On rows written before TM-9111 the
 *   object is the whole invoice.
 * - `WRITTEN_OFF` — an Ausbuchung (Bilanz).
 * - `CANCELLED` — the object was reversed: a Rechnung storniert, a Zahlung or
 *   an Ausbuchung reversed.
 */
export var DatevExportTrigger;
(function (DatevExportTrigger) {
    DatevExportTrigger["ISSUED"] = "ISSUED";
    DatevExportTrigger["PAID"] = "PAID";
    DatevExportTrigger["WRITTEN_OFF"] = "WRITTEN_OFF";
    DatevExportTrigger["CANCELLED"] = "CANCELLED";
})(DatevExportTrigger || (DatevExportTrigger = {}));
/**
 * The kind of domain object an `ExportedVoucher` row points to: a Rechnung,
 * a Zahlung on one, or an Ausbuchung of one (TM-9111).
 */
export var DatevExportObjectType;
(function (DatevExportObjectType) {
    DatevExportObjectType["PATIENT_INVOICE"] = "PATIENT_INVOICE";
    DatevExportObjectType["PAYMENT"] = "PAYMENT";
    DatevExportObjectType["WRITE_OFF"] = "WRITE_OFF";
})(DatevExportObjectType || (DatevExportObjectType = {}));
/**
 * How an `ExportedVoucher` row came to exist (PRD #1319 / RS-004).
 *
 * - `EXPORT` — the row was written by a real export run; a DATEV file
 *   contained this Buchungssatz. `datevExportId` points at that run.
 * - `MODE_SWITCH_CARRY_OVER` — the row was seeded at a Gewinnermittlungsart
 *   switch to record that DATEV *already knows* this revenue by the outgoing
 *   trigger's route, so the incoming mode must not re-book it. No file ever
 *   contained it, so `datevExportId IS NULL`.
 *
 * Invariant: `origin = 'EXPORT'` ⟺ `datevExportId IS NOT NULL`.
 */
export var DatevExportOrigin;
(function (DatevExportOrigin) {
    DatevExportOrigin["EXPORT"] = "EXPORT";
    DatevExportOrigin["MODE_SWITCH_CARRY_OVER"] = "MODE_SWITCH_CARRY_OVER";
})(DatevExportOrigin || (DatevExportOrigin = {}));
/**
 * Selbstzahler vs PKV split — drives the Sammeldebitor leg of every
 * Buchungssatz. Determined deterministically at export time from the
 * underlying invoice / treatment-plan payer-domain; never user-editable.
 *
 * v1 ships these two only because they map to the two Sammeldebitor-Konten
 * in `KontenMapping`. GKV / BG flow through entirely different processes
 * (Heilmittelabrechnungszentrum) and never reach the DATEV exporter.
 */
export var PayerType;
(function (PayerType) {
    PayerType["SELBSTZAHLER"] = "SELBSTZAHLER";
    PayerType["PKV"] = "PKV";
})(PayerType || (PayerType = {}));
/**
 * Lifecycle state of a single `DatevExport` row.
 *
 * The synchronous use-case is two-phase: phase 1 commits a claim row at
 * `IN_PROGRESS` so a concurrent caller sees it via
 * `findInFlightByOfficeLocationId` and bails with `ConflictException`; phase
 * 2 does the export work and transitions the row to `COMPLETED`. A crash or
 * domain-error between the two phases leaves the row in `IN_PROGRESS`; the
 * use-case's error path transitions it to `FAILED` so the in-flight guard
 * does not wedge subsequent runs.
 *
 * Backstop on the schema side: a per-OfficeLocation MySQL "named lock"
 * isn't available here (no partial unique indexes in InnoDB), so the
 * application-level guard plus `SELECT ... FOR UPDATE` in the claim phase
 * are the only line of defence; concurrent claimants serialise on the gap
 * lock and the second sees the first's committed `IN_PROGRESS` row.
 */
export var DatevExportStatus;
(function (DatevExportStatus) {
    DatevExportStatus["IN_PROGRESS"] = "IN_PROGRESS";
    DatevExportStatus["COMPLETED"] = "COMPLETED";
    DatevExportStatus["FAILED"] = "FAILED";
})(DatevExportStatus || (DatevExportStatus = {}));
//# sourceMappingURL=datev-export-enums.js.map